// src/service/websocket/NiftyWebsocketApi.jsx

import { useEffect, useState } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { environment } from "environment/environment"; // absolute import

const DEFAULT_STOCK_API_WEBSOCKET_URL = environment.stockApiDayRangeDataWSUrl;

const useStockApiWebsocket = (
    socketUrl = DEFAULT_STOCK_API_WEBSOCKET_URL
) => {
    const [options, setOptions] = useState([]);
    const [stockAnalysisData, setStockAnalysisData] = useState([]);
    const [stockSectorAnalysisData, setStockSectorAnalysisData] = useState([]);
    const [sectorialIndicesData, setSectorialIndicesData] = useState([]);
    const [strategyIndicesData, setStrategyIndicesData] = useState([]);
    const [globalMarketData, setGlobalMarketData] = useState([]);
    const [fiiDiiData, setFiiDiiData] = useState([]);
    const [foTopGainerLooserData, setFoTopGainerLooserData] = useState([]);
    const [blockDealsLargeStockData, setBlockDealsLargeStockData] = useState([]);
    const [blockDealsTopStockData, setBlockDealsTopStockData] = useState([]);
    const [logicalIndicatorValueData, setLogicalIndicatorValueData] = useState([]);
    const [buyerSellerStockData, setBuyerSellerStockData] = useState([]);
    const [stockScannerData, setStockScannerData] = useState([]);
    const [atlasScannerData, setAtlasScannerData] = useState([]);
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return; // SSR safety

        const stompClient = new Client({
            webSocketFactory: () => new SockJS(socketUrl),
            reconnectDelay: 5000, // auto-reconnect
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            debug: () => { }, // disable debug logs
        });

        stompClient.onConnect = () => {
            setIsConnected(true);
            const stockDayRangeDataTopic = environment.stoackApiTopic + environment.stockDayRangeDataTopic;

            stompClient.subscribe(stockDayRangeDataTopic, (message) => {
                try {
                    setOptions(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const stockAnalysisTopic = environment.stoackApiTopic + environment.stockDataAnalysisTopic;
            stompClient.subscribe(stockAnalysisTopic, (message) => {
                try {
                    setStockAnalysisData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const stockSectorTopic = environment.stoackApiTopic + environment.stockSectorTopic;
            stompClient.subscribe(stockSectorTopic, (message) => {
                try {
                    setStockSectorAnalysisData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const sectorIndicesTopic = environment.stoackApiTopic + environment.sectorIndicestopic;
            stompClient.subscribe(sectorIndicesTopic, (message) => {
                try {
                    setSectorialIndicesData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const strategyIndicesTopic = environment.stoackApiTopic + environment.strategyIndiciesTopic;
            stompClient.subscribe(strategyIndicesTopic, (message) => {
                try {
                    setStrategyIndicesData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const globalMarketTopic = environment.stoackApiTopic + environment.globalMarketDataTopic;
            stompClient.subscribe(globalMarketTopic, (message) => {
                try {
                    setGlobalMarketData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const fiiDiiTopic = environment.stoackApiTopic + environment.fiiDiiDataTopic;
            stompClient.subscribe(fiiDiiTopic, (message) => {
                try {
                    setFiiDiiData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const foTopGainerLooserTopic = environment.stoackApiTopic + environment.foTopGainerLooserTopic;
            stompClient.subscribe(foTopGainerLooserTopic, (message) => {
                try {
                    setFoTopGainerLooserData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const blockDealsLargeStockTopic = environment.stoackApiTopic + environment.blockDealsLargeStockTopic;
            stompClient.subscribe(blockDealsLargeStockTopic, (message) => {
                try {
                    setBlockDealsLargeStockData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const blockDealsTopStockTopic = environment.stoackApiTopic + environment.blockDealsTopStockTopic;
            stompClient.subscribe(blockDealsTopStockTopic, (message) => {
                try {
                    setBlockDealsTopStockData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const logicalIndicatorValueTopic = environment.stoackApiTopic + environment.logicalIndicatorValueTopic;
            stompClient.subscribe(logicalIndicatorValueTopic, (message) => {
                try {
                    setLogicalIndicatorValueData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const buyerSellerStockTopic = environment.stoackApiTopic + environment.buyerSellerStockTopic;
            stompClient.subscribe(buyerSellerStockTopic, (message) => {
                try {
                    setBuyerSellerStockData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const stockScannerTopic = environment.stoackApiTopic + environment.stockScannerTopic;
            stompClient.subscribe(stockScannerTopic, (message) => {
                try {
                    setStockScannerData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });

            const atlasScannerTopic = environment.stoackApiTopic + environment.atlasScannerTopic;
            stompClient.subscribe(atlasScannerTopic, (message) => {
                try {
                    setAtlasScannerData(JSON.parse(message.body));
                } catch (err) {
                    console.error("Failed to parse message:", err);
                }
            });
        };

        stompClient.onStompError = (frame) => {
            console.error("Broker reported error:", frame.headers["message"]);
            console.error("Additional details:", frame.body);
        };

        stompClient.onWebSocketError = (error) => {
            console.error("Stock websocket connection error:", error);
        };

        stompClient.activate();

        return () => {
            if (stompClient.active) {
                void stompClient.deactivate().catch((error) => {
                    console.error("Failed to close stock websocket connection:", error);
                });
            }
        };
    }, [socketUrl]);

    return { options, stockAnalysisData, stockSectorAnalysisData, sectorialIndicesData, strategyIndicesData, 
        isConnected, globalMarketData, fiiDiiData, foTopGainerLooserData, blockDealsLargeStockData, 
        blockDealsTopStockData, logicalIndicatorValueData, buyerSellerStockData, stockScannerData, atlasScannerData};
};

export default useStockApiWebsocket;
